<template>
  <div class="space-y-8">
    <!-- Notice Cards Section -->
    <div
      v-if="userStore.isRole(['admin', 'project_admin', 'company_admin', 'contract_admin', 'contract_manager'])"
      class="flex gap-6"
    >
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-full">
        <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

              <h3 class="text-lg font-semibold text-gray-900">
                Subcontract Settings
              </h3>
            </div>

            <button
              v-if="project?.unarchived"
              type="button"
              class="btn btn--primary-blue flex items-center space-x-2"
              data-test="save-subcontract-settings"
              :disabled="updateSubcontractSettingsAction.is(States.LOADING)"
              @click="updateSubcontractSettings"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>

              <span>
                Save Subcontract Settings
              </span>
            </button>
          </div>
        </div>

        <div class="p-6">
          <!-- Contract Configuration Section -->
          <div
            v-if="userStore.isRole(['admin', 'project_admin', 'company_admin', 'contract_admin'])"
            class="space-y-8"
          >
            <form
              method="post"
              @submit.prevent="updateSubcontractSettings"
            >
              <div class="flex gap-6">
                <!-- Contract Details -->
                <div class="space-y-4 w-1/2">
                  <h5 class="text-sm font-medium text-gray-900 flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-4 h-4 text-gray-500">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                    <span>Subcontract Settings</span>
                  </h5>

                  <div class="space-y-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        Claims Due On <span class="text-xs text-gray-500">(day of month)</span>
                      </label>

                      <AppInput
                        v-model="subcontractSettingsForm.form.claims_due_on"
                        :error="subcontractSettingsForm.getErrors('claims_due_on')"
                        class="form-input"
                        type="number"
                        name="claims_due_on"
                      />
                    </div>

                    <div
                      v-if="dateToSend"
                      class="p-3 bg-blue-50 rounded-lg border border-blue-200"
                    >
                      <p class="text-sm font-medium text-blue-900">
                        Claims should be issued to subcontractor on the {{ dateToSend }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden w-1/2">
        <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3">
          <div class="flex items-center space-x-2">
            <div class="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-7 h-7 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
</svg>
            </div>

            <h4 class="text-sm font-semibold text-gray-900">
              Receipt of Claim
            </h4>
          </div>
        </div>

        <div class="p-4">
          <form
            method="post"
            class="space-y-4"
            @submit.prevent="updateSubcontractSettings"
          >
            <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeRequired') }}</span>

              <input
                v-model="subcontractSettingsForm.form.receipt_of_claim_required"
                name="variation_submission_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSettings.HeadContractTab.label.noticePeriod') }} <span class="text-xs text-gray-500">({{ $t('ProjectSettings.HeadContractTab.hint.days') }})</span>
              </label>

              <AppInput
                v-model="subcontractSettingsForm.form.receipt_of_claim_period"
                class="form-input"
                type="number"
                name="receipt_of_claim_period"
                :error="subcontractSettingsForm.getErrors('receipt_of_claim_period')"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.daysType') }}</label>

              <div class="flex space-x-2">
                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[subcontractSettingsForm.form.receipt_of_claim_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="subcontractSettingsForm.form.receipt_of_claim_business_days = true"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.business') }}
                </button>

                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[!subcontractSettingsForm.form.receipt_of_claim_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="subcontractSettingsForm.form.receipt_of_claim_business_days = false"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.calendar') }}
                </button>
              </div>
            </div>

            <div
              v-if="project.isContractor"
              class="space-y-2"
            >
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeClause') }}</label>

              <AppTextArea
                v-model="subcontractSettingsForm.form.receipt_of_claim_clause"
                name="variation_submission_clause"
                class="form-input resize-none"
                :error="subcontractSettingsForm.getErrors('receipt_of_claim_clause')"
                enable-floating-error
                rows="3"
              />
            </div>

            <div
              v-if="project.isContractor"
              class="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
            >
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.additionalInformationRequired') }}</span>

              <input
                v-model="subcontractSettingsForm.form.receipt_of_claim_additional_info_required"
                name="receipt_of_claim_additional_info_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>
          </form>
        </div>
      </div>

      <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden w-1/2">
        <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3">
          <div class="flex items-center space-x-2">
            <div class="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-4 h-4 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
            </div>

            <h4 class="text-sm font-semibold text-gray-900">
              Issue of Certificate
            </h4>
          </div>
        </div>

        <div class="p-4">
          <form
            method="post"
            class="space-y-4"
            @submit.prevent="updateSubcontractSettings"
          >
            <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeRequired') }}</span>

              <input
                v-model="subcontractSettingsForm.form.issue_of_certificate_required"
                name=".issue_of_certificate_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSettings.HeadContractTab.label.noticePeriod') }} <span class="text-xs text-gray-500">({{ $t('ProjectSettings.HeadContractTab.hint.days') }})</span>
              </label>

              <AppInput
                v-model="subcontractSettingsForm.form.issue_of_certificate_period"
                class="form-input"
                type="number"
                name="issue_of_certificate_period"
                :error="subcontractSettingsForm.getErrors('issue_of_certificate_period')"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.daysType') }}</label>

              <div class="flex space-x-2">
                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[subcontractSettingsForm.form.issue_of_certificate_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="subcontractSettingsForm.form.issue_of_certificate_business_days = true"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.business') }}
                </button>

                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[!subcontractSettingsForm.form.issue_of_certificate_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="subcontractSettingsForm.form.issue_of_certificate_business_days = false"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.calendar') }}
                </button>
              </div>
            </div>

            <div
              v-if="project.isContractor"
              class="space-y-2"
            >
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeClause') }}</label>

              <AppTextArea
                v-model="subcontractSettingsForm.form.issue_of_certificate_clause"
                name="nov_clause"
                class="form-input resize-none"
                :error="subcontractSettingsForm.getErrors('issue_of_certificate_clause')"
                enable-floating-error
                rows="3"
              />
            </div>

            <div
              v-if="project.isContractor"
              class="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
            >
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.additionalInformationRequired') }}</span>

              <input
                v-model="subcontractSettingsForm.form.issue_of_certificate_additional_info_required"
                name="issue_of_certificate_additional_info_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>
          </form>
        </div>
      </div>

      <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden w-1/2">
        <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3">
          <div class="flex items-center space-x-2">
            <div class="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-4 h-4 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
            </div>

            <h4 class="text-sm font-semibold text-gray-900">
              {{ $t('ProjectSettings.SubcontractTab.header.sopaStatement') }}
            </h4>
          </div>
        </div>

        <div class="p-4">
          <form
            method="post"
            class="space-y-4"
            @submit.prevent="updateSubcontractSettings"
          >
            <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.SubcontractTab.label.enableSopaStatement') }}</span>

              <input
                v-model="subcontractSettingsForm.form.enable_sopa_statement"
                name="enable_sopa_statement"
                type="checkbox"
                data-test="enable-sopa-statement"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>

            <div
              v-if="subcontractSettingsForm.form.enable_sopa_statement"
              class="space-y-2"
            >
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.SubcontractTab.label.sopaStatementText') }}</label>

              <AppTextArea
                v-model="subcontractSettingsForm.form.sopa_statement_text"
                name="sopa_statement_text"
                class="form-input resize-none"
                data-test="sopa-statement-text"
                :error="subcontractSettingsForm.getErrors('sopa_statement_text')"
                enable-floating-error
                rows="3"
              />
            </div>
          </form>
        </div>
      </div>
    </div>

    <div
      v-if="userStore.isRole(['admin', 'project_admin', 'company_admin', 'contract_admin', 'contract_manager'])"
      class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
    >
      <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-gray-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
</svg>

            <h3 class="text-lg font-semibold text-gray-900">
              Procurement Steps
            </h3>
          </div>

          <button
            v-if="project?.unarchived"
            type="button"
            class="btn btn--secondary-blue"
            data-test="add-procurement-step"
            @click="addProcurementStep"
          >
            Add Step
          </button>
        </div>
      </div>

      <div class="p-6">
        <p class="text-sm text-gray-600 mb-2">
          Default steps used when on new packages
        </p>

        <p class="text-sm text-gray-600 mb-4">
          If the last step has an actual date filled, procurement is marked as complete.
        </p>

        <div
          v-if="subcontractSettingsForm.getErrors('procurement_steps')"
          class="mb-4 text-sm text-red-600"
        >
          {{ subcontractSettingsForm.getErrors('procurement_steps') }}
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Step
                </th>

                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                  Lead Time Weeks
                </th>

                <th class="px-3 py-2 w-24" />
              </tr>
            </thead>

            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="(step, index) in subcontractSettingsForm.form.procurement_steps"
                :key="index"
              >
                <td class="px-3 py-2">
                  <AppInput
                    v-model="step.name"
                    type="text"
                    :name="`procurement_steps.${index}.name`"
                    class="form-input"
                    :error="subcontractSettingsForm.getErrors(`procurement_steps.${index}.name`)"
                  />
                </td>

                <td class="px-3 py-2">
                  <AppInput
                    v-model="step.lead_time_weeks"
                    :name="`procurement_steps.${index}.lead_time_weeks`"
                    type="number"
                    class="form-input"
                    :error="subcontractSettingsForm.getErrors(`procurement_steps.${index}.lead_time_weeks`)"
                  />
                </td>

                <td class="px-3 py-2 text-right">
                  <button
                    type="button"
                    class="text-red-600 hover:text-red-800 text-sm"
                    data-test="remove-procurement-step"
                    @click="removeProcurementStep(index)"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput, falsey, getOrdinalSuffix, Holiday, MONTHS, plusBusinessDays } from '@/helpers';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { log } from 'console';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps<{
  projectId: string;
}>();

const emits = defineEmits<{
  (e: 'dirty');
  (e: 'saved');
}>();

const projectsStore = useProjectsStore();

const subcontractSettingsForm = useFormData({
  receipt_of_claim_clause: '',
  receipt_of_claim_required: false,
  receipt_of_claim_additional_info_required: false,
  receipt_of_claim_period: 0,
  receipt_of_claim_business_days: false,
  issue_of_certificate_clause: '',
  issue_of_certificate_required: false,
  issue_of_certificate_additional_info_required: false,
  issue_of_certificate_period: 0,
  issue_of_certificate_business_days: false,
  claims_due_on: 0,
  enable_sopa_statement: true,
  sopa_statement_text: '',
  procurement_steps: [] as { name: string; lead_time_weeks: number }[],
});

const project = computed(() => {
  return projectsStore.project;
});

watch(project, (newValue) => {
  if(newValue) {
    subcontractSettingsForm.setData(newValue.subcontract_settings);

    if(!subcontractSettingsForm.form.procurement_steps?.length) {
      subcontractSettingsForm.form.procurement_steps = [];
    }

    subcontractSettingsForm.resetErrors();

    nextTick(() => {
      subcontractSettingsForm.resetDirtyStatus();
    });
  }
}, { immediate: true });

const addProcurementStep = () => {
  subcontractSettingsForm.form.procurement_steps.push({
    name: '',
    lead_time_weeks: 1,
  });
};

const removeProcurementStep = (index: number) => {
  subcontractSettingsForm.form.procurement_steps.splice(index, 1);
};

watch(subcontractSettingsForm.isDirty, (newValue) => {
  if(newValue) {
    emits('dirty');
  } else {
    emits('saved');
  }
});

const updateSubcontractSettingsAction = useStoreApiAction(projectsStore.updateSubcontractSettings);

const updateSubcontractSettings = () => {
  subcontractSettingsForm.resetErrors();

  updateSubcontractSettingsAction.request(props.projectId, subcontractSettingsForm.form).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Subcontract Settings Updated.',
    });
  }).catch((error) => {
    subcontractSettingsForm.setErrorsFromAxiosError(error);
  });
};

const userStore = useUserStore();

const dateToSend = computed(() => {
  const claimsDueDate = DateTime.now().set({
    day: falsey(subcontractSettingsForm.form.claims_due_on) ? 0 : subcontractSettingsForm.form.claims_due_on,
  });

  const sendOutDate = subcontractSettingsForm.form.receipt_of_claim_business_days ?
    plusBusinessDays(
      claimsDueDate,
      project.value?.head_contract_settings.holidays ?? [],
      -subcontractSettingsForm.form.receipt_of_claim_period,
    ) :
    claimsDueDate.minus({
      days: subcontractSettingsForm.form.receipt_of_claim_period,
    });

  const dateDue = sendOutDate.day;
  const month = MONTHS[sendOutDate.month - 1];

  return `${dateDue}${getOrdinalSuffix(dateDue)} of ${month}`;
});
</script>

<style scoped></style>
